import React, {useEffect, useMemo, useRef, useState} from 'react';
import {fileUploadText} from '../fileUploadText';
import TipBox from '../../../uiToolkit/TipBox';
import {
  FILE_FHD,
  FILE_HD,
  FILE_SD_OPTION,
  FILE_UHD,
  mapResolutionKeyToUIKey,
  mapUIKeyToResolutionKey,
} from '../../../data/models/LibraryFile/MediaFile';
import {getVideoDimentions} from '../../../utils/getVideoDimentions';
import Button from '../../../uiToolkit/Buttons/Button';
import {loadingLottie} from '../../../assets/lottie';
import Lottie from 'lottie-react';
import ModalContainer from '../../../uiToolkit/ModalContainer';
import CardOptionsInput from '../../../uiToolkit/Inputs/CardOptionsInput';
import './styles.css';

interface Props {
  file: File;
  onFileUpload: (duration: number, targetRes: number) => void;
  isLoading: boolean;
  nonVideoLoading: boolean;
  maxAllowedRes: number;
  onClose: () => void;
  position?: number;
  total?: number;
  initialRes?: number;
  onBack?: () => void;
  isUploading?: boolean;
}

const HD_HEIGHT = 720;
const FHD_HEIGHT = 1080;
const UHD_HEIGHT = 2160;

const FileUploadModalBodyUI = React.memo(
  ({
    file,
    onFileUpload,
    isLoading,
    nonVideoLoading,
    maxAllowedRes,
    onClose,
    position,
    total,
    initialRes,
    onBack,
    isUploading,
  }: Props) => {
    const [duration, setDuration] = useState(0);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);
    const [targetRes, setTargetRes] = useState(
      mapResolutionKeyToUIKey(initialRes ?? 0),
    );

    const videoEl = useRef<HTMLVideoElement>(null);

    const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);

    useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

    useEffect(() => {
      if (file.type.includes('video')) {
        getVideoDimentions(file).then(res => {
          setWidth(res.width);
          setHeight(res.height);
        });
      }
    }, [file]);

    const handleLoadedMetadata = () => {
      const video = videoEl.current;
      if (!video) return;
      if (video.duration && !duration) {
        setDuration(video.duration);
      }
    };

    useEffect(() => {
      const video = videoEl.current;
      if (!video || !file.type.includes('video')) return;

      const paintFrame = () => {
        if (
          !video.currentTime &&
          video.duration &&
          Number.isFinite(video.duration)
        ) {
          video.currentTime = Math.min(0.1, video.duration / 2);
        }
      };

      video.addEventListener('loadeddata', paintFrame);
      video.addEventListener('canplay', paintFrame);
      video.load();

      return () => {
        video.removeEventListener('loadeddata', paintFrame);
        video.removeEventListener('canplay', paintFrame);
      };
    }, [previewUrl, nonVideoLoading]);

    const getDropdownOptions = () => {
      return [FILE_SD_OPTION, FILE_HD, FILE_FHD, FILE_UHD];
    };

    const getDropdownBlockedOptions = () => {
      const result = [];
      if (height < HD_HEIGHT || maxAllowedRes < FILE_HD.internalKey) {
        result.push(FILE_HD);
      }
      if (height < FHD_HEIGHT || maxAllowedRes < FILE_FHD.internalKey) {
        result.push(FILE_FHD);
      }
      if (height < UHD_HEIGHT || maxAllowedRes < FILE_UHD.internalKey) {
        result.push(FILE_UHD);
      }
      return result;
    };

    const onUpload = () => {
      onFileUpload(duration, mapUIKeyToResolutionKey(targetRes));
    };

    const renderVideo = useMemo(() => {
      return (
        <video
          src={previewUrl}
          ref={videoEl}
          preload="auto"
          muted
          playsInline
          onLoadedMetadata={handleLoadedMetadata}
        />
      );
    }, [previewUrl]);

    return (
      <ModalContainer onClose={onClose} hideCloseButton={isUploading}>
        {nonVideoLoading ? (
          <div className="d-flex flex-row justify-content-center">
            <Lottie
              animationData={loadingLottie}
              loop={true}
              style={{
                width: '40px',
                height: '40px',
                marginBottom: '10px',
              }}
            />
          </div>
        ) : (
          <div className="FileUploadModalBody">
            {!!total && total > 1 && !!position && (
              <div className="UploadSteps">
                <p className="UploadStepsLabel">
                  {fileUploadText.fileStep(position, total)}
                </p>
                <div className="UploadStepsDots">
                  {Array.from({length: total}, (_, index) => (
                    <span
                      key={index}
                      className={`UploadStepDot ${
                        index < position ? 'UploadStepDotDone' : ''
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
            <h3 className="UploadingTitle">{fileUploadText.uploadingFile}</h3>
            <div className="PreviewContainer">
              <div className="VideoFileContainer">
                {renderVideo}
                <p className="FileTitle">{file?.name}</p>
                <p className="FileResolution">
                  {width}x{height}
                </p>
              </div>
              <div className="FileUploadTip">
                <TipBox
                  tip={
                    <p>
                      <p className="mb-2">{fileUploadText.tip3}</p>
                      <p className="mb-2">{fileUploadText.tip}</p>
                      <p>{fileUploadText.tip2}</p>
                    </p>
                  }
                />
              </div>
            </div>
            <h4>{fileUploadText.selectHeader}</h4>
            <CardOptionsInput
              options={getDropdownOptions()}
              selectedOption={targetRes}
              onSelect={setTargetRes}
              disabledOptions={getDropdownBlockedOptions()}
            />
            <div className="Footer">
              {onBack && !isUploading && (
                <Button
                  title={fileUploadText.back}
                  onClick={onBack}
                  uiType="secondary"
                />
              )}
              <Button
                title={
                  total && position && position < total
                    ? fileUploadText.next
                    : fileUploadText.upload
                }
                onClick={onUpload}
                isLoading={isLoading || isUploading}
              />
            </div>
          </div>
        )}
      </ModalContainer>
    );
  },
);

export default FileUploadModalBodyUI;
